/**
 * Registr routovacích providerů: výběr podle preference a konfigurace,
 * paměťová cache výsledků, per-provider „circuit breaker" (po 2 selháních
 * 60 s klidu) a řetězení: selže-li preferovaný provider, zkusí se další.
 *
 * Rozšíření o dalšího providera = nový soubor s implementací RoutingProvider
 * a přidání do ALL_PROVIDERS. Nic dalšího se měnit nemusí.
 */
import type { LatLng, RoutingProfile } from '../../core/types';
import type { RouteGeometry } from '../../core/planner';
import { orsProvider } from './ors';
import { osrmProvider } from './osrm';
import type { ProviderConfig, ProviderId, RoutingProvider } from './types';

export type { ProviderConfig, ProviderId, RoutingProvider } from './types';

export const ALL_PROVIDERS: RoutingProvider[] = [osrmProvider, orsProvider];

export type ProviderPreference = 'auto' | ProviderId;

export interface RoutingOptions extends ProviderConfig {
  online: boolean;
  /** Alias pro osrmEndpoint (zpětná kompatibilita). */
  endpoint?: string;
  provider?: ProviderPreference;
}

export type RoutingHealth = 'ok' | 'degraded' | 'offline';

/* ---------------- interní stav ---------------- */

const cache = new Map<string, RouteGeometry>();
const failures = new Map<ProviderId, number>();
const blockedUntil = new Map<ProviderId, number>();

const keyOf = (profile: RoutingProfile, a: LatLng, b: LatLng): string =>
  `${profile}:${a.lat.toFixed(5)},${a.lng.toFixed(5)}→${b.lat.toFixed(5)},${b.lng.toFixed(5)}`;

function cfgOf(opts: RoutingOptions): ProviderConfig {
  return {
    timeoutMs: opts.timeoutMs,
    osrmEndpoint: opts.osrmEndpoint ?? opts.endpoint,
    orsApiKey: opts.orsApiKey ?? envOrsKey(),
  };
}

/** Klíč z env (Vite) — bezpečná konfigurace bez zápisu do kódu. */
export function envOrsKey(): string {
  try {
    return (import.meta.env?.VITE_ORS_API_KEY as string | undefined) ?? '';
  } catch {
    return '';
  }
}

/** Seřazený seznam použitelných providerů podle preference. */
export function eligibleProviders(opts: RoutingOptions): RoutingProvider[] {
  const cfg = cfgOf(opts);
  const configured = ALL_PROVIDERS.filter((p) => p.isConfigured(cfg));
  if (opts.provider && opts.provider !== 'auto') {
    const chosen = configured.filter((p) => p.id === opts.provider);
    // Explicitně zvolený, ale nenakonfigurovaný provider → žádný fallback,
    // ať je stav viditelný (UI ukáže „chybí konfigurace").
    return chosen;
  }
  return configured;
}

/** Aktuální zdraví routování (pro stavový čip v UI). */
export function routingHealth(opts: RoutingOptions): RoutingHealth {
  if (!opts.online) return 'offline';
  const providers = eligibleProviders(opts);
  if (providers.length === 0) return 'offline';
  const now = Date.now();
  const allBlocked = providers.every((p) => now < (blockedUntil.get(p.id) ?? 0));
  if (allBlocked) return 'degraded';
  const anyTrouble = providers.some(
    (p) => (failures.get(p.id) ?? 0) > 0 || now < (blockedUntil.get(p.id) ?? 0),
  );
  return anyTrouble ? 'degraded' : 'ok';
}

/** Synchronní čtení z cache — používá plánovač (GeometryProvider). */
export function cachedRoute(
  profile: RoutingProfile,
  a: LatLng,
  b: LatLng,
): RouteGeometry | undefined {
  return cache.get(keyOf(profile, a, b));
}

/**
 * Asynchronní načtení trasy do cache přes řetěz providerů.
 * Vrací true, když přibylo něco nového. Nikdy nevyhazuje výjimku.
 */
export async function prefetchRoute(
  profile: RoutingProfile,
  a: LatLng,
  b: LatLng,
  opts: RoutingOptions,
): Promise<boolean> {
  const key = keyOf(profile, a, b);
  if (cache.has(key)) return false;
  if (!opts.online) return false;

  const cfg = cfgOf(opts);
  for (const provider of eligibleProviders(opts)) {
    if (Date.now() < (blockedUntil.get(provider.id) ?? 0)) continue;
    try {
      const r = await provider.route(profile, a, b, cfg);
      cache.set(key, {
        geometry: r.geometry,
        distanceKm: r.distanceKm,
        durationMin: r.durationMin,
        real: true,
        source: provider.id,
      });
      failures.set(provider.id, 0);
      return true;
    } catch {
      const n = (failures.get(provider.id) ?? 0) + 1;
      failures.set(provider.id, n);
      if (n >= 2) {
        // 60 s klidu pro tento provider; další v řetězu se zkusí hned.
        blockedUntil.set(provider.id, Date.now() + 60_000);
      }
    }
  }
  return false;
}

/** Reset stavu (testy, změna endpointu/klíče). */
export function resetRouting(): void {
  cache.clear();
  failures.clear();
  blockedUntil.clear();
}
